import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HedgehogLogo } from '../components/brand/HedgehogLogo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { AlertCircle, Lock, Mail, ArrowRight } from 'lucide-react';

export const LoginView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/dashboard';
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email.trim(), password);
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cf-bg flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="flex justify-center mb-3">
          <HedgehogLogo size={42} />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-cf-textPrimary">
          Sign in to Ericius
        </h2>
        <p className="mt-1 text-xs text-cf-textSecondary">
          Multi-tenant form ingestion & relational datastore platform
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="bg-cf-surface py-8 px-6 border-cf-border shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="mb-5 p-3 rounded bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-xs text-rose-600 dark:text-rose-400 font-medium">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email Address"
              size="xs"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              leftIcon={<Mail size={14} />}
            />

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-cf-textSecondary">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-cf-orange hover:text-cf-orangeHover transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                size="xs"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                leftIcon={<Lock size={14} />}
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                fullWidth
                disabled={isLoading}
                isLoading={isLoading}
                rightIcon={!isLoading ? <ArrowRight size={14} /> : undefined}
              >
                Sign In
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-5 border-t border-cf-border text-center">
            <p className="text-xs text-cf-textSecondary">
              Don't have an organization yet?{' '}
              <Link
                to="/register"
                className="text-cf-orange hover:text-cf-orangeHover font-medium transition-colors"
              >
                Create an account
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
